import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";
import { Calendar, ChevronRight } from "lucide-react";

const RANGES = [
    { label: "7d", days: 7 },
    { label: "30d", days: 30 },
    { label: "90d", days: 90 },
    { label: "1y", days: 365 },
    { label: "All", days: null },
];

const FILTERS = [
    { value: "all", label: "All" },
    { value: "order", label: "Orders" },
    { value: "prescription", label: "Prescriptions" },
    { value: "subscription", label: "Subscriptions" },
    { value: "chat", label: "Chat" },
    { value: "wallet", label: "Wallet" },
    { value: "return", label: "Returns" },
];

const TYPE_TONES = {
    order: "border-l-brand-500",
    prescription: "border-l-emerald-500",
    subscription: "border-l-violet-500",
    chat: "border-l-sky-500",
    wallet: "border-l-amber-500",
    return: "border-l-rose-500",
};

const relTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
};

const HealthTimeline = () => {
    const [filter, setFilter] = useState("all");
    const [range, setRange] = useState(90);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const pageSize = 30;

    const types = useMemo(() => (filter === "all" ? null : filter), [filter]);

    useEffect(() => { setPage(1); }, [filter, range]);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (types) params.set("types", types);
        if (range) params.set("range_days", String(range));
        params.set("page", String(page));
        params.set("page_size", String(pageSize));
        api.get(`/health-timeline?${params.toString()}`)
            .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
            .finally(() => setLoading(false));
    }, [types, range, page]);

    return (
        <AuthGuard>
            <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
                <Navbar />
                <main className="mx-auto max-w-3xl px-6 py-12 lg:px-12">
                    <p className="label-uppercase text-zinc-500">Your journey</p>
                    <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-50" style={{ letterSpacing: "-0.02em" }}>
                        Health Timeline
                    </h1>
                    <p className="mt-2 max-w-xl text-sm text-zinc-500">
                        Everything in one place — orders, prescriptions, subscriptions, AI chats, wallet credits and returns. Your health story on PILLS.
                    </p>

                    {/* Filters */}
                    <div className="mt-8 flex flex-wrap gap-1.5" data-testid="timeline-type-filters">
                        {FILTERS.map((f) => (
                            <button
                                key={f.value}
                                onClick={() => setFilter(f.value)}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                    filter === f.value ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:bg-white/5 dark:text-zinc-300"
                                }`}
                                data-testid={`timeline-filter-${f.value}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                        <Calendar className="h-3 w-3" />
                        <span>Range:</span>
                        {RANGES.map((r) => (
                            <button
                                key={r.label}
                                onClick={() => setRange(r.days)}
                                className={`rounded-md px-2 py-0.5 ${range === r.days ? "bg-zinc-900 text-white dark:bg-white/10 dark:text-zinc-50" : "border border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:bg-white/5"}`}
                                data-testid={`timeline-range-${r.label}`}
                            >
                                {r.label}
                            </button>
                        ))}
                        <span className="ml-auto">{total} event{total === 1 ? "" : "s"}</span>
                    </div>

                    {/* Events */}
                    <ol className="relative mt-10 space-y-4" data-testid="timeline-events">
                        {/* vertical rule */}
                        <span className="absolute left-3 top-1 bottom-1 w-px bg-zinc-200 dark:bg-white/10" aria-hidden />
                        {loading ? (
                            <li className="ml-12 text-sm text-zinc-500">Loading…</li>
                        ) : items.length === 0 ? (
                            <li className="ml-0">
                                <EmptyState
                                    title="Nothing here yet"
                                    description="Your health journey will appear here as you use PILLS — orders, prescriptions, chats, and more."
                                />
                            </li>
                        ) : items.map((e) => (
                            <li key={e.id} className="relative pl-12" data-testid={`timeline-event-${e.id}`}>
                                <span className="absolute left-0 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white text-base shadow ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-white/10">
                                    {e.icon}
                                </span>
                                <div className={`rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm border-l-4 ${TYPE_TONES[e.type] || "border-l-zinc-300"} dark:bg-white/[0.03] dark:border-white/10`}>
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-50">{e.title}</p>
                                            <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">{e.description}</p>
                                        </div>
                                        <span className="text-[10px] text-zinc-500" title={new Date(e.timestamp).toLocaleString()}>{relTime(e.timestamp)}</span>
                                    </div>
                                    {e.action_link && (
                                        <Link to={e.action_link} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline" data-testid={`timeline-event-link-${e.id}`}>
                                            Open <ChevronRight className="h-3 w-3" />
                                        </Link>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>

                    {/* Pagination */}
                    {total > pageSize && (
                        <div className="mt-8 flex justify-center gap-2">
                            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm disabled:opacity-50">Previous</button>
                            <span className="px-2 py-1.5 text-sm text-zinc-500">Page {page} of {Math.ceil(total / pageSize)}</span>
                            <button disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm disabled:opacity-50">Next</button>
                        </div>
                    )}
                </main>
                <Footer />
            </div>
        </AuthGuard>
    );
};

export default HealthTimeline;
